import { FC, useMemo } from 'react'
import ArticlePush, { ArticlePushProps } from 'src/components/ArticlePush'
import Router from 'src/routes/Router'
import routes from 'src/routes/definitions'
import { readableDate } from 'src/helpers/DateHelpers'
import { useTranslation } from 'react-i18next'
import { secondsToMinute } from 'src/helpers/TimeHelpers'
import { ImageTransformer } from 'src/relay/Transformers/ImageTransformer'
import { ButtonTransformer } from 'src/relay/Transformers/ButtonTransormer'
import { RichTextTransformer } from 'src/relay/Transformers/RichTextTransformer'

import { BlockProps } from '../props'

import { HighlightArticleBlockProps } from './props'

const PictureColumnContent: FC<BlockProps<HighlightArticleBlockProps>> = ({
  data,
  locale,
  configuration,
}) => {
  const { t } = useTranslation()

  // Strapi v4 wraps relations as `{ data: { id, attributes } }`; v5 returns
  // them flat. Unwrap both shapes once and work off `attrs`.
  const articleRaw: any = (data?.article as any)?.data ?? data?.article
  const articleAttrs: any = articleRaw?.attributes ?? articleRaw
  const projectRaw: any = (data?.projet as any)?.data ?? data?.projet
  const projectAttrs: any = projectRaw?.attributes ?? projectRaw

  const props: ArticlePushProps = useMemo(() => {
    // Tags: v4 = `tags.data[].attributes.name`, v5 = `tags[].name`.
    const unwrapTags = (tagsField: any): any[] =>
      tagsField?.data ?? tagsField ?? []
    const tagName = (tag: any): string => tag?.attributes?.name ?? tag?.name
    // Hero media: unwrap the outer `.data` so the flat case works. The image
    // transformer itself is already shape-agnostic for the inner `attributes`.
    const heroMedia = (attrs: any, field: string) =>
      attrs?.hero?.[field]?.data ?? attrs?.hero?.[field]

    return {
      ...(data.title && {
        blockTitleProps: {
          title: data.title,
          actionButtonProps: ButtonTransformer({ data: data.cta }),
        },
      }),
      cards: [
        ...(articleAttrs
          ? [
              {
                ...(unwrapTags(articleAttrs?.tags).length > 0 && {
                  tags: unwrapTags(articleAttrs?.tags).map((tag: any) => ({
                    text: t('article.tag', { tag: tagName(tag) }),
                  })),
                }),
                pictureProps: {
                  imageProps: ImageTransformer(
                    heroMedia(articleAttrs, 'desktopImage'),
                    { tablet: '100vw, 40vw' }
                  ),
                  mobileImageProps: ImageTransformer(
                    heroMedia(articleAttrs, 'mobileImage'),
                    { tablet: '100vw, 40vw' }
                  ),
                },
                ...(articleAttrs?.hero?.description && {
                  richTextProps: RichTextTransformer(
                    articleAttrs.hero?.description,
                    configuration,
                    locale
                  ),
                }),
                ...(articleAttrs?.createdAt && {
                  date: readableDate(articleAttrs.createdAt),
                }),
                ...(articleAttrs?.readTimeInSeconds && {
                  readingTime: t('article.time', {
                    minutes: secondsToMinute(articleAttrs.readTimeInSeconds),
                  }),
                }),
                ...(articleAttrs?.slug && {
                  linkProps: {
                    href: Router.getRouteUrl(routes.articles, {
                      slug: articleAttrs.slug ?? '',
                    }),
                    locale,
                  },
                }),
              },
            ]
          : []),
        ...(projectAttrs
          ? [
              {
                ...(unwrapTags(projectAttrs?.tags).length > 0 && {
                  tags: unwrapTags(projectAttrs?.tags).map((tag: any) => ({
                    text: t('article.tag', { tag: tagName(tag) }),
                  })),
                }),
                pictureProps: {
                  imageProps: ImageTransformer(
                    heroMedia(projectAttrs, 'desktopImage'),
                    { tablet: '100vw, 40vw' }
                  ),
                  mobileImageProps: ImageTransformer(
                    heroMedia(projectAttrs, 'mobileImage'),
                    { tablet: '100vw, 40vw' }
                  ),
                },
                ...(projectAttrs?.description && {
                  richTextProps: RichTextTransformer(
                    projectAttrs.description,
                    configuration,
                    locale
                  ),
                }),
                ...(projectAttrs?.createdAt && {
                  date: readableDate(projectAttrs.createdAt),
                }),
                ...(projectAttrs?.slug && {
                  linkProps: {
                    href: Router.getRouteUrl(routes.projects, {
                      slug: projectAttrs.slug ?? '',
                      lang: locale,
                    }),
                    locale,
                  },
                }),
              },
            ]
          : []),
      ],
    }
  }, [configuration, data, locale, t, projectAttrs, articleAttrs])

  return <ArticlePush {...props} />
}

export default PictureColumnContent
